import React, { ReactNode } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Fade,
  useTheme,
  useMediaQuery
} from '@mui/material';
import SentimentGauge from './SentimentGauge';

interface SentimentCardProps {
  score: number;
  sentimentLevel: {
    level: string;
    color: string;
  };
  titleComponent?: ReactNode;
  displayMode?: 'sentiment' | 'reputation';
  animateChart?: boolean;
  size?: 'compact' | 'standard';
  isPdfMode?: boolean;
  onGaugeRendered?: (chartName: string, imageDataUrl: string) => void;
  chartName?: string;
}

const SentimentCard: React.FC<SentimentCardProps> = ({ 
  score, 
  sentimentLevel,
  titleComponent,
  displayMode = 'sentiment',
  animateChart = true,
  size = 'standard',
  isPdfMode = false,
  onGaugeRendered,
  chartName
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const getDisplayText = () => {
    if (displayMode === 'reputation') {
      return sentimentLevel.level;
    } else {
      return `${sentimentLevel.level} Reception`;
    }
  };
  
  return (
    <Card 
      elevation={3} 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        // Prevent layout shifts on mobile
        minHeight: { xs: '300px', sm: '350px', md: '400px' },
        maxHeight: { xs: '400px', sm: '450px', md: '500px' },
        // Ensure stable width
        width: '100%',
        overflow: 'hidden' // Prevent content from overflowing
      }}
    >
      <CardContent 
        sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'space-between', // Changed from center to space-between
          alignItems: 'center', 
          py: { xs: 2, md: 3 },
          px: { xs: 1.5, md: 2 },
          // Prevent content from overflowing
          overflow: 'hidden',
          height: '100%'
        }}
      >
        {/* Title Section - Fixed height */}
        <Box sx={{ 
          width: '100%', 
          mb: { xs: 1, md: 2 },
          minHeight: { xs: '60px', md: '80px' }, // Reserve space for title
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {titleComponent || (
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              gutterBottom 
              sx={{ 
                textAlign: 'center',
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                lineHeight: 1.2,
                mb: 0
              }}
            >
              {displayMode === 'reputation' ? 'Reputation Impact' : 'Audience Sentiment'}
            </Typography>
          )}
        </Box>

        {/* Gauge Section - Flexible height */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          width: '100%', 
          flex: 1,
          // Constrain the gauge area to prevent overflow
          minHeight: { xs: '180px', md: '220px' },
          maxHeight: { xs: '220px', md: '280px' },
          position: 'relative'
        }}>
          <Box sx={{ 
            width: '100%', 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <SentimentGauge 
              score={score} 
              sentimentLevel={sentimentLevel} 
              displayMode={displayMode} 
              animateChart={animateChart && !isPdfMode}
              size={isMobile && !isPdfMode ? 'compact' : size}
              isPdfMode={isPdfMode}
              onGaugeRendered={onGaugeRendered}
              chartName={chartName || (displayMode === 'reputation' ? 'reputationGauge' : 'sentimentGauge')}
            />
          </Box>
        </Box>

        {/* Label Section - Fixed height */}
        <Box sx={{ 
          minHeight: { xs: '40px', md: '50px' }, // Reserve space for label
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mt: { xs: 1, md: 2 }
        }}>
          <Fade in={animateChart} timeout={500}>
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"}
              sx={{ 
                color: sentimentLevel.color,
                fontWeight: 'bold',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                textAlign: 'center',
                lineHeight: 1.2
              }}
            >
              {getDisplayText()}
            </Typography>
          </Fade>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SentimentCard; 